// src/pages/Clients.js
import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import API_URL from "../config";
import {
  FaUserPlus,
  FaUsers,
  FaTrash,
  FaEdit,
  FaTimes,
  FaCheckCircle,
  FaFilter,
} from "react-icons/fa";
import "./Clients.css";

export default function Clients({ entrepriseId, siteId }) {
  const [clients, setClients] = useState([]);
  const [produits, setProduits] = useState([]);
  const [nom, setNom] = useState("");
  const [telephone, setTelephone] = useState(""); // garde pour API
  const [selectedProduits, setSelectedProduits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [messageBox, setMessageBox] = useState({ visible: false, type: "success", text: "" });
  const [filterNom, setFilterNom] = useState("");
  const [filterProduit, setFilterProduit] = useState(null);

  const normalizeId = (id) => String(id ?? "");

  const showMessage = (type, text) => {
    setMessageBox({ visible: true, type, text });
    setTimeout(() => setMessageBox((m) => ({ ...m, visible: false })), 3000);
  };

  const fetchProduits = async () => {
    if (!entrepriseId || !siteId) return;
    try {
      const res = await fetch(`${API_URL}/api/produits?entrepriseId=${entrepriseId}&siteId=${siteId}`);
      if (!res.ok) throw new Error("Erreur produits");
      const data = await res.json();
      const normalized = Array.isArray(data)
        ? data.map((p) => ({ id: normalizeId(p.id), nom: p.nom ?? p.name ?? "" }))
        : [];
      normalized.sort((a, b) => (a.nom || "").localeCompare(b.nom || ""));
      setProduits(normalized);
    } catch (err) {
      console.error(err);
      showMessage("error", "Erreur chargement produits");
    }
  };

  const fetchClients = async () => {
  if (!entrepriseId || !siteId) return;
  try {
    const res = await fetch(`${API_URL}/api/clients?entrepriseId=${entrepriseId}&siteId=${siteId}`);
    if (!res.ok) throw new Error();
    const data = await res.json();

    console.log("✅ Données clients reçues :", data);

    // ✅ Le backend renvoie déjà un tableau [{ id, nom, produits: [...] }]
    const normalized = Array.isArray(data)
      ? data.map((c) => ({
          id: c.id,
          nom: c.nom,
          entrepriseId: c.entrepriseId,
          siteId: c.siteId,
          date_creation: c.date_creation,
          produits: Array.isArray(c.produits)
            ? c.produits.map((p) => ({
                produit_id: String(p.produit_id ?? p.id ?? ""),
                produit_nom: p.produit_nom ?? p.nom ?? "",
                prix_grossiste: p.prix_grossiste ?? "",
              }))
            : [],
        }))
      : [];

    setClients(normalized);
    setError(null);
  } catch (err) {
    console.error(" Erreur chargement clients :", err);
    setError("Erreur chargement clients");
    showMessage("error", "Erreur chargement clients");
  }
};

  useEffect(() => {
    fetchProduits();
    fetchClients();
  }, [entrepriseId, siteId]);

  const resetForm = () => {
    setNom("");
    setSelectedProduits([]);
    setEditId(null);
  };

  const openDialog = (client = null) => {
    if (client) {
      setEditId(client.id);
      setNom(client.nom || "");
      setTelephone(client.telephone || ""); // garde pour API
      setSelectedProduits(
        (client.produits || []).map((p) => ({
          id: normalizeId(p.produit_id),
          label: p.produit_nom ?? "Produit",
          prix: p.prix_grossiste != null ? String(p.prix_grossiste) : "",
        }))
      );
    } else resetForm();
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setTimeout(resetForm, 120);
  };

  const handleProduitSelect = (options) => {
    if (!options) return setSelectedProduits([]);
    setSelectedProduits(
      options.map((opt) => ({
        id: normalizeId(opt.value),
        label: opt.label,
        prix: "",
      }))
    );
  };

  const handlePrixChange = (id, prix) =>
    setSelectedProduits((prev) =>
      prev.map((sp) => (normalizeId(sp.id) === normalizeId(id) ? { ...sp, prix } : sp))
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nom || selectedProduits.length === 0) {
      showMessage("error", "Nom et au moins un produit sont requis.");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        nom,
        telephone, // toujours envoyé
        entrepriseId,
        siteId,
        produits: selectedProduits.map((sp) => ({
          produit_id: isNaN(Number(sp.id)) ? sp.id : Number(sp.id),
          prix_grossiste: sp.prix === "" ? null : parseFloat(sp.prix),
        })),
      };
      const res = await fetch(editId ? `${API_URL}/api/clients/${editId}` : `${API_URL}/api/clients`, {
        method: editId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      await fetchClients();
      closeDialog();
      showMessage("success", editId ? "Client mis à jour." : "Client ajouté.");
    } catch {
      showMessage("error", "Erreur lors de l’enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    try {
      const res = await fetch(`${API_URL}/api/clients/${id}?entrepriseId=${entrepriseId}&siteId=${siteId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error();
      await fetchClients();
      showMessage("success", "Client supprimé");
    } catch {
      showMessage("error", "Erreur de suppression");
    }
  };

  const combinedProduits = useMemo(() => {
    const map = new Map();
    produits.forEach((p) => map.set(normalizeId(p.id), { id: normalizeId(p.id), nom: p.nom }));
    clients.forEach((c) =>
      (c.produits || []).forEach((p) => {
        const pid = normalizeId(p.produit_id);
        if (!map.has(pid)) map.set(pid, { id: pid, nom: p.produit_nom ?? "Produit" });
      })
    );
    return Array.from(map.values()).sort((a, b) => a.nom.localeCompare(b.nom));
  }, [produits, clients]);

  const produitOptions = useMemo(() => combinedProduits.map((p) => ({ value: p.id, label: p.nom })), [combinedProduits]);

  const produitIds = combinedProduits.map((p) => p.id);

  const clientsWithPrixMap = useMemo(
    () =>
      clients.map((c) => {
        const prixMap = {};
        (c.produits || []).forEach((p) => (prixMap[normalizeId(p.produit_id)] = p.prix_grossiste ?? ""));
        produitIds.forEach((pid) => (prixMap[pid] ??= ""));
        return { ...c, prixMap };
      }),
    [clients, produitIds]
  );

  const filteredClients = useMemo(
    () =>
      clientsWithPrixMap.filter((c) => {
        const nomOK = c.nom.toLowerCase().includes(filterNom.toLowerCase());
        const prodOK =
          !filterProduit ||
          (c.produits || []).some((p) => normalizeId(p.produit_id) === normalizeId(filterProduit.value));
        return nomOK && prodOK;
      }),
    [clientsWithPrixMap, filterNom, filterProduit]
  );

  const formatPrix = (p) => (!p ? "-" : !isNaN(Number(p)) ? Number(p).toFixed(2) : p);

  return (
    <div className="clients-page">
      <h1 className="page-title">
        <FaUsers className="icon-title" /> Gestion des Clients
      </h1>

      <button className="btn-add-client" onClick={() => openDialog()}>
        <FaUserPlus style={{ marginRight: 6 }} /> Ajouter un client
      </button>

      <div className="filters">
        <h3>
          <FaFilter style={{ marginRight: 6 }} /> Filtres
        </h3>
        <div className="filters-row">
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={filterNom}
            onChange={(e) => setFilterNom(e.target.value)}
            className="filter-input"
          />
          <Select
            className="filter-select"
            placeholder="Filtrer par produit..."
            options={produitOptions}
            isClearable
            value={filterProduit}
            onChange={setFilterProduit}
          />
        </div>
      </div>

      <div className="clients-table-container">
        <h2>Liste des clients ({filteredClients.length})</h2>
        {error && <div className="error">{error}</div>}
        <div className="table-wrapper" style={{ overflowX: "auto" }}>
          <table className="clients-table">
            <thead>
              <tr>
                <th>Client</th>
                {combinedProduits.map((p) => (
                  <th key={p.id}>{p.nom}</th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={combinedProduits.length + 2} style={{ textAlign: "center" }}>
                    Aucun client trouvé
                  </td>
                </tr>
              ) : (
                filteredClients.map((c, index) => (
                  <tr key={c.id} className={index % 2 === 0 ? "row-even" : "row-odd"}>
                    <td>{c.nom}</td>
                    {combinedProduits.map((p) => (
                      <td key={p.id}>{formatPrix(c.prixMap[normalizeId(p.id)])}</td>
                    ))}
                    <td className="actions">
                      <button className="action-icon edit" title="Modifier" onClick={() => openDialog(c)}>
                        <FaEdit />
                      </button>
                      <button className="action-icon delete" title="Supprimer" onClick={() => handleDelete(c.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDialog && (
        <div className="dialog-overlay">
          <div className="dialog dialog-scrollable">
            <div className="dialog-header">
              <h2>{editId ? "Modifier le client" : "Ajouter un client"}</h2>
              <button className="close-btn" onClick={closeDialog}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="dialog-form">
              <div className="form-row">
                <label>Nom complet *</label>
                <input value={nom} onChange={(e) => setNom(e.target.value)} />
              </div>
              <div className="form-row">
                <label>Produits *</label>
                <Select
                  isMulti
                  options={produitOptions}
                  value={selectedProduits.map((sp) => ({ value: sp.id, label: sp.label }))}
                  onChange={handleProduitSelect}
                  placeholder="Sélectionnez un ou plusieurs produits"
                />
              </div>
              {selectedProduits.map((sp) => (
                <div className="form-row" key={sp.id}>
                  <label>Prix pour {sp.label}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={sp.prix}
                    onChange={(e) => handlePrixChange(sp.id, e.target.value)}
                  />
                </div>
              ))}
              <div className="form-actions">
                <button className="btn-primary" type="submit" disabled={loading}>
                  {editId ? (loading ? "Mise à jour..." : "Mettre à jour") : loading ? "Ajout..." : "Ajouter"}
                </button>
                <button type="button" className="btn-secondary" onClick={closeDialog}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {messageBox.visible && (
        <div className={`message-box ${messageBox.type}`}>
          <div className="message-content">
            {messageBox.type === "success" ? <FaCheckCircle /> : <FaTimes />}
            <span>{messageBox.text}</span>
            <button className="close-btn" onClick={() => setMessageBox({ ...messageBox, visible: false })}>
              <FaTimes />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
